import { Reducer } from 'react';

import {
    PlayTypeNameEnum,
    ProgramTypeEnum,
    ProgramTypeNameEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { isEmpty, isEqual, isUndefined, size } from 'lodash';
import { ServiceId } from 'src/constants';
import { AT_MOST_N_SELECTIONS, EXACTLY_1_ON_DEMAND_DEFINITION, MUST_N_SELECTIONS } from 'src/constants/errorMessages';
import { ROUTES } from 'src/constants/routes';
import { IMetadata } from 'src/types/IMetadata';
import {
    IPrimeAMFRateCardValidationAction,
    IPrimeAMFValidateAdRevShareAction,
    IPrimeAMFValidateCostPerPlayAction,
    IPrimeAMFValidateCppRatesAction,
    IPrimeAMFValidateCustomerTypeAction,
    IPrimeAMFValidateIsNextDisabledAction,
    IPrimeAMFValidateMauRatesAction,
    IPrimeAMFValidateMonthlyActiveUserAction,
    IPrimeAMFValidatePlayTypeAction,
    IPrimeAMFValidateProgramTypeAction,
    IPrimeAMFValidatePrsDeductionCapAction,
    IPrimeAMFValidateRevShareAction
} from 'src/types/IPrimeAMFRateCardValidationAction';
import { IRateCardGridRowData } from 'src/types/IRateCard';
import { getCamelStrFromAllCaps } from 'src/util/RateCards';

export interface IPrimeAMFRateCardErrors {
    isNextDisabled: boolean;
    primeContractTerms: {
        costPerPlay: {
            model: string;
            playTypes: string;
            customerTypes: string;
            programTypes: string;
            conversions: string;
        };
        monthlyActiveUser: {
            model: string;
            customerTypes: string;
        };
        prsDeductionCap: string;
    };
    amfContractTerms: {
        adRevShare: {
            model: string;
            revSharePercent: string;
            conversions: string;
            playTypes: string;
        };
    };
    rateCardRowData: {
        cpp: Set<string>;
        mau: Set<string>;
    };
}

export const DEFAULT_PRIME_AMF_RATE_CARD_ERRORS: IPrimeAMFRateCardErrors = {
    isNextDisabled: false,
    primeContractTerms: {
        costPerPlay: {
            model: '',
            playTypes: '',
            customerTypes: '',
            programTypes: '',
            conversions: ''
        },
        monthlyActiveUser: {
            model: '',
            customerTypes: ''
        },
        prsDeductionCap: ''
    },
    amfContractTerms: {
        adRevShare: {
            model: '',
            revSharePercent: '',
            conversions: '',
            playTypes: ''
        }
    },
    rateCardRowData: {
        cpp: new Set(),
        mau: new Set()
    }
};

export const rateCardValidationReducer: Reducer<IPrimeAMFRateCardErrors, IPrimeAMFRateCardValidationAction> = (
    rateCardErrors,
    action
): IPrimeAMFRateCardErrors => {
    switch (action.type) {
        case 'validate_is_next_disabled':
            return validateIsNextDisabled(rateCardErrors, action);

        case 'validate_play_type':
            return validatePlayType(rateCardErrors, action);

        case 'validate_customer_type':
            return validateCustomerType(rateCardErrors, action);

        case 'validate_program_type':
            return validateProgramType(rateCardErrors, action);

        case 'validate_monthly_active_user':
            return validateMonthlyActiveUser(rateCardErrors, action);

        case 'validate_cost_per_play':
            return validateCostPerPlay(rateCardErrors, action);

        case 'validate_prs_deduction_cap':
            return validatePrsDeductionCap(rateCardErrors, action);

        case 'validate_ad_rev_share':
            return validateAdRevShare(rateCardErrors, action);

        case 'validate_rev_share':
            return validateRevShare(rateCardErrors, action);

        case 'validate_cpp_rates':
            return validateCppRates(rateCardErrors, action);

        case 'validate_mau_rates':
            return validateMauRates(rateCardErrors, action);

        // Invalid action
        default:
            throw Error(`Unknown action: ${action}`);
    }
};

const validateIsNextDisabled = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateIsNextDisabledAction
): IPrimeAMFRateCardErrors => {
    let isNextDisabled = false;

    // Prime
    if (action.rateCardValues.serviceId === ServiceId.Prime) {
        isNextDisabled = [
            action.rateCardValues.primeContractTerms?.costPerPlay?.playTypes,
            action.rateCardValues.primeContractTerms?.costPerPlay?.customerTypes,
            action.rateCardValues.primeContractTerms?.costPerPlay?.programTypeList,
            action.rateCardValues.primeContractTerms?.costPerPlay?.conversions,
            action.rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType,
            action.rateCardValues.primeContractTerms?.adRevShare?.revSharePercent
        ].every((attr) => isEmpty(attr));
    }

    // AMF
    else {
        isNextDisabled =
            isEmpty(action.rateCardValues.amfContractTerms?.adRevShare.revSharePercent) ||
            [
                action.rateCardValues.amfContractTerms?.adRevShare.playTypes,
                action.rateCardValues.amfContractTerms?.adRevShare.conversions
            ].every((attr) => isEmpty(attr));
    }

    return {
        ...prevRateCardErrors,
        isNextDisabled: isNextDisabled || containsErrorMessages(prevRateCardErrors, action.stepId)
    };
};

const validatePlayType = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidatePlayTypeAction
): IPrimeAMFRateCardErrors => {
    const playTypes =
        action.rateCardValues.serviceId === ServiceId.Prime
            ? (action.rateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [])
            : (action.rateCardValues.amfContractTerms?.adRevShare?.playTypes ?? []);
    const onDemand = playTypes.find((pt) => pt.playTypeName === PlayTypeNameEnum.ON_DEMAND);

    let errorMessage = '';
    // Toggled but no selection made
    if (action.isToggled && isEmpty(playTypes)) {
        errorMessage = MUST_N_SELECTIONS(1);
    } else if (!isUndefined(onDemand) && onDemand.playTypeDefinitions!.length != 1) {
        /*
         * Check if rate card has only one on demand definition. Ideally, rate cards
         * already only have one on-demand definition associated, but this is a data
         * quality check
         */
        errorMessage = EXACTLY_1_ON_DEMAND_DEFINITION;
    }

    const newRateCardErrors: IPrimeAMFRateCardErrors =
        action.rateCardValues.serviceId === ServiceId.Prime
            ? {
                  ...prevRateCardErrors,
                  primeContractTerms: {
                      ...prevRateCardErrors.primeContractTerms,
                      costPerPlay: {
                          ...prevRateCardErrors.primeContractTerms.costPerPlay,
                          playTypes: errorMessage
                      }
                  }
              }
            : {
                  ...prevRateCardErrors,
                  isNextDisabled: false,
                  amfContractTerms: {
                      ...prevRateCardErrors.amfContractTerms,
                      adRevShare: {
                          ...prevRateCardErrors.amfContractTerms.adRevShare,
                          playTypes: errorMessage
                      }
                  }
              };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateCustomerType = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateCustomerTypeAction
): IPrimeAMFRateCardErrors => {
    const customerTypes = action.isMAU
        ? (action.rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType ?? [])
        : (action.rateCardValues.primeContractTerms?.costPerPlay?.customerTypes ?? []);

    const errorMessage = action.isToggled && isEmpty(customerTypes) ? MUST_N_SELECTIONS(1) : '';

    const newRateCardErrors: IPrimeAMFRateCardErrors = action.isMAU
        ? {
              ...prevRateCardErrors,
              primeContractTerms: {
                  ...prevRateCardErrors.primeContractTerms,
                  monthlyActiveUser: {
                      ...prevRateCardErrors.primeContractTerms.monthlyActiveUser,
                      customerTypes: errorMessage
                  }
              }
          }
        : {
              ...prevRateCardErrors,
              primeContractTerms: {
                  ...prevRateCardErrors.primeContractTerms,
                  costPerPlay: {
                      ...prevRateCardErrors.primeContractTerms.costPerPlay,
                      customerTypes: errorMessage
                  }
              }
          };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateProgramType = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateProgramTypeAction
): IPrimeAMFRateCardErrors => {
    const selectedProgramTypes = action.rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.filter(
        (pt) => pt.programTypeName !== ProgramTypeNameEnum.DEFAULT
    );

    let errorMessage = '';
    if (action.isToggled && isEmpty(selectedProgramTypes)) {
        errorMessage = MUST_N_SELECTIONS(1);
    } else if (action.isToggled && size(selectedProgramTypes) > 1) {
        errorMessage = AT_MOST_N_SELECTIONS(1);
    }

    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        primeContractTerms: {
            ...prevRateCardErrors.primeContractTerms,
            costPerPlay: {
                ...prevRateCardErrors.primeContractTerms.costPerPlay,
                programTypes: errorMessage
            }
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateMonthlyActiveUser = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateMonthlyActiveUserAction
): IPrimeAMFRateCardErrors => {
    const errorMessage =
        action.isToggled && isEmpty(action.rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType)
            ? MUST_N_SELECTIONS(1)
            : '';

    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        primeContractTerms: {
            ...prevRateCardErrors.primeContractTerms,
            monthlyActiveUser: {
                ...prevRateCardErrors.primeContractTerms.monthlyActiveUser,
                model: errorMessage
            }
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateCostPerPlay = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateCostPerPlayAction
): IPrimeAMFRateCardErrors => {
    const errorMessage =
        action.isToggled &&
        [
            action.rateCardValues.primeContractTerms?.costPerPlay?.playTypes,
            action.rateCardValues.primeContractTerms?.costPerPlay?.programTypeList,
            action.rateCardValues.primeContractTerms?.costPerPlay?.customerTypes,
            action.rateCardValues.primeContractTerms?.costPerPlay?.conversions
        ].every((key) => isEmpty(key))
            ? MUST_N_SELECTIONS(1)
            : '';

    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        primeContractTerms: {
            ...prevRateCardErrors.primeContractTerms,
            costPerPlay: {
                ...prevRateCardErrors.primeContractTerms.costPerPlay,
                model: errorMessage
            }
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validatePrsDeductionCap = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidatePrsDeductionCapAction
): IPrimeAMFRateCardErrors => {
    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        primeContractTerms: {
            ...prevRateCardErrors.primeContractTerms,
            prsDeductionCap: action.message
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateAdRevShare = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateAdRevShareAction
): IPrimeAMFRateCardErrors => {
    let errorMessage = '';

    if (action.isToggled) {
        // For Prime service, only revenue share percentage is required
        if (action.rateCardValues.serviceId === ServiceId.Prime) {
            errorMessage = isEmpty(action.rateCardValues.primeContractTerms?.adRevShare?.revSharePercent)
                ? MUST_N_SELECTIONS(1)
                : '';
        }
        // For AMF service, play types or conversions are required
        else {
            errorMessage = [
                action.rateCardValues.amfContractTerms?.adRevShare.playTypes,
                action.rateCardValues.amfContractTerms?.adRevShare.conversions
            ].every((key) => isEmpty(key))
                ? MUST_N_SELECTIONS(1)
                : '';
        }
    }

    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        amfContractTerms: {
            ...prevRateCardErrors.amfContractTerms,
            adRevShare: {
                ...prevRateCardErrors.amfContractTerms.adRevShare,
                model: errorMessage
            }
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateRevShare = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateRevShareAction
): IPrimeAMFRateCardErrors => {
    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        amfContractTerms: {
            ...prevRateCardErrors.amfContractTerms,
            adRevShare: {
                ...prevRateCardErrors.amfContractTerms.adRevShare,
                revSharePercent: action.message
            }
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
    });
};

const validateCppRates = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateCppRatesAction
): IPrimeAMFRateCardErrors => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const visibleColumns = new Set(action.visibleColumns.map((col) => col.field!));
    const cppErrors = getGridErrors(action.rateCardValues.rateCardRowData.cpp, visibleColumns, action.metadata);

    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        rateCardRowData: {
            ...prevRateCardErrors.rateCardRowData,
            cpp: cppErrors
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CORE_RATES
    });
};

const validateMauRates = (
    prevRateCardErrors: IPrimeAMFRateCardErrors,
    action: IPrimeAMFValidateMauRatesAction
): IPrimeAMFRateCardErrors => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const visibleColumns = new Set(action.visibleColumns.map((col) => col.field!));
    const mauErrors = getGridErrors(action.rateCardValues.rateCardRowData.mau, visibleColumns);

    const newRateCardErrors: IPrimeAMFRateCardErrors = {
        ...prevRateCardErrors,
        rateCardRowData: {
            ...prevRateCardErrors.rateCardRowData,
            mau: mauErrors
        }
    };

    return validateIsNextDisabled(newRateCardErrors, {
        type: 'validate_is_next_disabled',
        rateCardValues: action.rateCardValues,
        stepId: ROUTES.RATE_CARDS.STEP.CORE_RATES
    });
};

const getGridErrors = (
    grid: IRateCardGridRowData[],
    visibleColumns: Set<string>,
    metadata?: IMetadata
): Set<string> => {
    const stringTypeFields = new Set([
        'rateCardCurrency',
        'settlementCurrency',
        'cae',
        'playType',
        'programType',
        'paperPlanName'
    ]);

    return new Set(
        grid.reduce((accumulator, current, rowIndex) => {
            const errorFields = new Set(visibleColumns);

            Object.entries(current).forEach(([columnName, columnValue]) => {
                // Skip territory ID
                if (columnName !== 'territoryId' && visibleColumns.has(columnName)) {
                    if (!isEmpty(columnValue) && (stringTypeFields.has(columnName) || !isNaN(Number(columnValue)))) {
                        errorFields.delete(columnName);
                    }
                }
            });

            // Special handling for DE Radio Play to skip validation on threshold rates
            // threshold rates are not required for On-Demand and DE Radio Play row
            if (
                current.playType === getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND) &&
                current.programType === ProgramTypeEnum.DE_RADIO_PLAY
            ) {
                errorFields.forEach((field) => {
                    if (field.startsWith('threshold')) {
                        errorFields.delete(field);
                    }
                });
            }

            // Skip validation on threshold rates on JP row as threshold rates are not always required on JP
            if (metadata?.territoryMapping[current.territoryId].territoryCode === 'JP') {
                errorFields.forEach((field) => {
                    if (field.startsWith('threshold')) {
                        errorFields.delete(field);
                    }
                });
            }

            const rowErrors = Array.from(errorFields)
                .filter((col) => col !== 'territoryId')
                .map((col) => generateGridErrorId(rowIndex, col));

            return [...accumulator, ...rowErrors];
        }, Array<string>())
    );
};

export const generateGridErrorId = (row: number, column: string): string => `${row}-*-${column}`;

const containsErrorMessages = (
    rateCardErrors: IPrimeAMFRateCardErrors,
    stepId: typeof ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS | typeof ROUTES.RATE_CARDS.STEP.CORE_RATES
): boolean => {
    const expected =
        stepId === ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
            ? {
                  primeContractTerms: DEFAULT_PRIME_AMF_RATE_CARD_ERRORS.primeContractTerms,
                  amfContractTerms: DEFAULT_PRIME_AMF_RATE_CARD_ERRORS.amfContractTerms
              }
            : DEFAULT_PRIME_AMF_RATE_CARD_ERRORS.rateCardRowData;
    const actual =
        stepId === ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
            ? {
                  primeContractTerms: rateCardErrors.primeContractTerms,
                  amfContractTerms: rateCardErrors.amfContractTerms
              }
            : rateCardErrors.rateCardRowData;

    return !isEqual(expected, actual);
};
